/**
 * Query Error Handler
 * Provides consistent error handling for React Query hooks
 */

import { normalizeError, isRetryableError } from "./errorHandler.js";

/**
 * Determines if an error should trigger a retry
 * @param {Error} error - The error object
 * @param {number} failureCount - Current failure count
 * @returns {boolean} True if should retry
 */
export const shouldRetryQuery = (error, failureCount) => {
    // Don't retry more than 2 times
    if (failureCount >= 2) {
        return false;
    }

    // Normalize error to check if it's retryable
    const normalizedError = normalizeError(error);
    
    // Check if it's a retryable error type
    if (isRetryableError(normalizedError)) {
        return true;
    }

    // Check for network errors (no response)
    if (!error.response) {
        return true;
    }

    const status = error.response?.status;

    // Retry on server errors (5xx)
    if (status >= 500 && status < 600) {
        return true;
    }

    // Retry on timeout (408)
    if (status === 408) {
        return true;
    }

    // Retry on rate limiting (429) - but only once
    if (status === 429 && failureCount < 1) {
        return true;
    }

    // Don't retry on client errors (4xx except 408, 429)
    return false;
};

/**
 * Creates a consistent onError handler for React Query
 * @param {Object} options - Configuration options
 * @param {Function} options.onError - Custom error callback
 * @param {string} options.queryName - Name of the query for logging
 * @param {Function} options.setModalOpen - Optional function to open error modal
 * @returns {Function} Error handler function
 */
export const createQueryErrorHandler = ({
    onError,
    queryName = 'Unknown Query',
    setModalOpen = null,
} = {}) => {
    return (error) => {
        const normalizedError = normalizeError(error);
        
        // Log error for debugging
        console.error(`[${queryName}] Query error:`, {
            message: normalizedError.message,
            type: normalizedError.type,
            originalError: normalizedError.originalError,
        });

        // Handle specific error types
        if (normalizedError.originalError?.response?.status === 401) {
            // Session expired - could trigger re-authentication flow
            console.warn(`[${queryName}] Session expired`);
        }

        if (normalizedError.originalError?.response?.status === 403) {
            // Permission denied
            console.warn(`[${queryName}] Permission denied`);
        }

        // Call custom error handler if provided
        if (onError) {
            onError(normalizedError, error);
        }

        // Open modal if provided and error requires it
        if (setModalOpen && normalizedError.type === 'unauthorized') {
            setModalOpen(true);
        }
    };
};

/**
 * Creates default query options with consistent error handling
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether query is enabled
 * @param {number} options.staleTime - Stale time in milliseconds
 * @param {number} options.cacheTime - Cache time in milliseconds
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {string} options.queryName - Query name for logging
 * @param {Function} options.setModalOpen - Optional function to open error modal
 * @param {number|Function} options.retry - Retry configuration (default: shouldRetryQuery)
 * @returns {Object} React Query options
 */
export const createQueryOptions = ({
    enabled = true,
    staleTime = 1000 * 60 * 5, // 5 minutes
    cacheTime = 1000 * 60 * 10, // 10 minutes
    onSuccess = null,
    onError = null,
    queryName = 'Unknown Query',
    setModalOpen = null,
    retry = shouldRetryQuery,
} = {}) => {
    const errorHandler = createQueryErrorHandler({
        onError,
        queryName,
        setModalOpen,
    });

    return {
        enabled,
        staleTime,
        cacheTime,
        retry,
        onSuccess,
        onError: errorHandler,
    };
};

